import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Progress } from "@/components/ui/progress";
import { Loader2, CheckCircle, Clock, AlertCircle } from "lucide-react";

interface PreviewProgressProps {
  videoId: string;
  status?: string;
  progress?: number;
}

export default function PreviewProgress({ videoId, status: initialStatus, progress: initialProgress = 0 }: PreviewProgressProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(initialStatus || 'uploaded');
  const [progress, setProgress] = useState(initialProgress);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update local state when props change
  useEffect(() => {
    setStatus(initialStatus || 'uploaded');
    setProgress(initialProgress);
  }, [initialStatus, initialProgress]);

  // Auto-scroll when progress starts to show
  // Auto-scroll logic - COMMENTED OUT to prevent unwanted scrolling
  // useEffect(() => {
  //   if (status === 'processing' || status === 'uploading' || status === 'full_processing') {
  //     setTimeout(() => {
  //       console.log('🚀 Auto-scrolling to progress bar for status:', status);
  //       containerRef.current?.scrollIntoView({ 
  //         behavior: 'smooth', 
  //         block: 'center' 
  //       });
  //       // Add additional offset to prevent menu overlap
  //       window.scrollBy(0, 30);
  //       console.log('✅ Scrolled to progress bar successfully');
  //     }, 100);
  //   }
  // }, [status]);

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />;
      case 'full_processing':
        return <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />;
      case 'preview_ready':
        return <CheckCircle className="h-8 w-8 text-green-600" />;
      case 'error':
        return <AlertCircle className="h-8 w-8 text-red-600" />;
      default:
        return <Clock className="h-8 w-8 text-blue-600" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'uploading':
        return 'Uploading video chunks...';
      case 'processing':
        return 'Processing preview with AI...';
      case 'full_processing':
        return 'Processing full video with AI...';
      case 'preview_ready':
        return 'Preview ready!';
      case 'error':
        return 'Processing failed';
      default:
        return 'Preparing preview...';
    }
  };

  const getProgressText = () => {
    if (status === 'preview_ready') {
      return 'Your video preview is ready to watch';
    }
    if (status === 'full_processing') {
      return 'Processing your full video... This may take several minutes';
    }
    if (status === 'error') {
      return 'Something went wrong. Please try again.';
    }
    return `${progress}% complete`;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" ref={containerRef} data-progress-section>
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          {getStatusIcon()}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {getStatusText()}
        </h3>
        <p className="text-gray-600 mb-4">
          {getProgressText()}
        </p>
        
        {/* Progress bar for processing */}
        {status === 'processing' && (
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        )}
        
        {/* Processing steps */}
        {status === 'processing' && (
          <div className="space-y-2 text-sm text-gray-500">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Extracting audio</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Generating subtitles</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
              <span>Creating preview</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


